import type { DashboardUpdate } from "@/commons/types/api/schema.types";
import { createAlert } from "@/follaw-fr/services/apiAlerts";
import { updateDashboard } from "@/follaw-fr/services/apiDashboards";
import { setOnboardingCompleted } from "@/follaw-fr/services/apiOnboarding";
import { patchFollowFeuilleVerteOdj } from "@/follaw-fr/services/apiUsers";
import { useUserStore } from "@/follaw-fr/stores/user";

import { storeToRefs } from "pinia";
import { type Ref } from "vue";

const JORF_DEFAULT_FILTERS: DashboardUpdate["filters"] = {
  jorf: {
    nature: ["Arrêté", "Décret", "Loi", "Rapport", "Ordonnance"],
  },
};

export type SetupAlertsParams = {
  followFeuilleVerte: boolean;
  digestJorf: boolean;
  alertTranscription: boolean;
};

const useOnboardingAlerts = () => {
  const { selectedDashboardId, selectedDashboardFilters } = storeToRefs(
    useUserStore()
  ) as {
    selectedDashboardId: Ref<number | null>; // or whatever type it should be
    selectedDashboardFilters: any; // All filters
  };
  const userStore = useUserStore();

  /** Onboarding - alert: follow Feuille Verte and ODJ */
  const patchFollowFeuilleVerteOdjPromise = () => patchFollowFeuilleVerteOdj();

  /** Onboarding - set filters for 'Journal Officiel' */
  const updateDashboardPromise = () =>
    updateDashboard(selectedDashboardId.value as number, {
      filters: {
        ...selectedDashboardFilters.value,
        ...JORF_DEFAULT_FILTERS,
      },
    });

  /** Onboarding - alert: create first alert on transcription */
  const createAlertPromise = () =>
    createAlert({
      name: "Ma première alerte",
      active: true,
      dashboard_id: selectedDashboardId.value as number,
      alert_services: ["transcription"],
      notification_type: "email",
    });

  const setupAlerts = async ({
    followFeuilleVerte,
    digestJorf,
    alertTranscription,
  }: SetupAlertsParams): Promise<SetupAlertsParams | null> => {
    // Sans veille (étape 1 non faite), rien n'est créé et l'onboarding n'est pas terminé : on rend null.
    if (!selectedDashboardId.value) {
      return null;
    }

    await Promise.all([
      ...(followFeuilleVerte ? [patchFollowFeuilleVerteOdjPromise()] : []),
      ...(digestJorf ? [updateDashboardPromise()] : []),
      ...(alertTranscription ? [createAlertPromise()] : []),
    ]);

    await setOnboardingCompleted();
    await userStore.getUserAndFilters();
    // Ce qui a réellement été créé : l'écran « Terminé » ne raconte que ça.
    return { followFeuilleVerte, digestJorf, alertTranscription };
  };

  return { setupAlerts };
};

export default useOnboardingAlerts;
